import { exists, UnreachableError } from 'base/helpers';
import {
  addPromiseToCache,
  CacheState,
  LibraryCache,
} from 'base/library_cache';
import { extractTreeByName } from 'base/webflow/data_attributes';
import {
  getClassIdToNameMap,
  getClassIdToStyleLessMap,
} from 'base/webflow/helpers';
import { NestedNode } from 'base/webflow/schema';
import { getSlotInfoAtNode } from 'blocks/component_renderer/slots';
import { syncSlotOverrides } from 'blocks/component_renderer/slots_syncing';
import { ConceptAPI } from 'blocks/editor/styleguide/concept_store';
import { UndoManager } from 'blocks/woot_woot/undo';
import { observable } from 'mobx';
import {
  ElementInstance,
  ElementInstanceState,
  ImageRef,
  ImageRefState,
  Page,
  SectionRef,
} from '../model';
import { allSlugsInElementTree, unrefSection } from '../model_helpers';
import { ProjectSession } from '../session/session';
import { ProjectAssetStore } from './asset_store';

export type RenderedImage = {
  src: string;
  blurredSrc?: string;
  thumbnailSrc?: string;
  aspectRatio?: string;
  width?: number;
  height?: number;
};

export class ImageRegistry {
  private renderedImagesCache = observable.map<
    string,
    CacheState<RenderedImage>
  >();
  private preloadedImagesCache = observable.map<string, CacheState<{}>>();

  constructor(private readonly projectAssetStore: ProjectAssetStore) {}

  renderImage(
    imageRef: ImageRefState | ImageRef,
  ): CacheState<RenderedImage | null> {
    let key: string;
    switch (imageRef.type) {
      case 'private':
        key = `private-${imageRef.id}`;
        break;
      case 'placeholder':
        key = `placeholder-${imageRef.id}`;
        break;
      case 'unsplash':
        // TODO: include crop properties in the cache key?
        key = `unsplash-${imageRef.base}`;
        break;
      default:
        throw new UnreachableError(imageRef);
    }

    if (this.renderedImagesCache.has(key)) {
      return this.renderedImagesCache.get(key)!;
    }

    switch (imageRef.type) {
      case 'private':
        const asset = this.projectAssetStore.getProjectImage(imageRef.id);

        return addPromiseToCache(
          this.renderedImagesCache,
          key,
          asset.promise.then((asset) => {
            if (!asset) return null;
            return {
              src: asset.fullSizeUrl,
              thumbnailSrc: asset.thumbnailUrl,
              aspectRatio: `${asset.width} / ${asset.height}`,
              width: asset.width,
              height: asset.height,
            };
          }),
        );

      case 'unsplash':
        let crop = '';
        const { width, height } = imageRef;
        if (imageRef.properties.crop.length === 4) {
          const [x, y, w, h] = imageRef.properties.crop;
          crop = `&rect=${Math.round((x * width) / 100)},${Math.round(
            (y * height) / 100,
          )},${Math.round((w * width) / 100)},${Math.round(
            (h * height) / 100,
          )}`;
        }

        return addPromiseToCache(
          this.renderedImagesCache,
          key,
          Promise.resolve({
            src: `${imageRef.base}${crop}&auto=format&q=80&w=640`,
            aspectRatio: `${width} / ${height}`,
            width,
            height,
          }),
        );

      case 'placeholder':
        return addPromiseToCache(
          this.renderedImagesCache,
          key,
          Promise.resolve({
            src: getPlaceholderImageUrl(imageRef.id),
            blurredSrc: getPlaceholderBlurredImageUrl(imageRef.id) ?? undefined,
          }),
        );

      default:
        throw new UnreachableError(imageRef);
    }
  }

  preloadImage(url: string) {
    if (this.preloadedImagesCache.has(url)) {
      return this.preloadedImagesCache.get(url)!;
    }

    return addPromiseToCache(
      this.preloadedImagesCache,
      url,
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({});
        img.onerror = reject;
        img.src = url;
      }),
    );
  }
}

/**
 * Returns a key representing a particular image in a wireframe whose source can
 * be overridden.
 */
export const getImageKey = (
  element: ElementInstance | ElementInstanceState,
  node: NestedNode,
) => `${element.id}:${node._id}`;

const PLACEHOLDER_IMAGE_SETS = {
  default: [
    'default-1',
    'default-2',
    'default-3',
    'default-4',
    'default-5',
    'default-6',
    'default-7',
    'default-8',
    'default-9',
    'default-10',
    'default-11',
    'default-12',
    'default-13',
    'default-14',
    'default-15',
    'default-16',
    'default-17',
    'default-18',
    'default-19',
    'default-20',
    'default-21',
    'default-22',
    'default-23',
    'default-24',
    'default-25',
    'default-26',
    'default-27',
    'default-28',
    'default-29',
    'default-30',
    'default-31',
    'default-32',
    'default-33',
    'default-34',
    'default-35',
    'default-36',
    'default-37',
    'default-38',
    'default-39',
    'default-40',
    'default-41',
    'default-42',
    'default-43',
    'default-44',
    'default-45',
    'default-46',
    'default-47',
  ],
  team: [
    'team-1',
    'team-2',
    'team-3',
    'team-4',
    'team-5',
    'team-6',
    'team-7',
    'team-8',
  ],
  testimonial: [
    'testimonial-1',
    'testimonial-2',
    'testimonial-3',
    'testimonial-4',
    'testimonial-5',
    'testimonial-6',
  ],
  contact: ['contact-1', 'contact-2', 'contact-3', 'contact-4'],
  timeline: [
    'timeline-1',
    'timeline-2',
    'timeline-3',
    'timeline-4',
    'timeline-5',
    'timeline-6',
    'timeline-7',
    'timeline-8',
  ],
  portfolio: [
    'portfolio-1',
    'portfolio-2',
    'portfolio-3',
    'portfolio-4',
    'portfolio-5',
    'portfolio-6',
  ],
  background: [
    'background-1',
    'background-2',
    'background-3',
    'background-4',
    'background-5',
    'background-6',
    'background-7',
    'background-8',
    'background-9',
    'background-10',
    'background-11',
    'background-12',
  ],
};

// Components where we want to override the choice of image set.
// Takes precedence over detection of background images, but not avatars.
const SPECIAL_CASES: Record<string, keyof typeof PLACEHOLDER_IMAGE_SETS> = {
  section_links3: 'background',
  section_links4: 'testimonial',
};

export enum ImageNodeType {
  Logo = 'logo',
  Icon = 'icon',
  Overridable = 'overridable',
  Unknown = 'unknown',
}

const ICON_CLASS_RE = /[-_]icon|icon[-_]/;
const PLACEHOLDER_LOGO_URL_RE = /Logo-wide\.svg$/;
const OVERRIDABLE_WITHOUT_PLACEHOLDER_URL_RE =
  /(logo-(relume|webflow)|Company%20Name)\.svg$/;

export type ImageReplacementMetadata =
  | { type: ImageNodeType.Logo; key: string }
  | { type: ImageNodeType.Icon; key: string }
  | {
      type: ImageNodeType.Overridable;
      placeholderImageSet: keyof typeof PLACEHOLDER_IMAGE_SETS | null;
      key: string;
    }
  | { type: ImageNodeType.Unknown };

export const getImageReplacementMetadata = (
  element: ElementInstance | ElementInstanceState,
  node: NestedNode,
  {
    classIdToNameMap,
    classIdToStyleMap,
  }: {
    classIdToNameMap: ReadonlyMap<string, string>;
    classIdToStyleMap: ReadonlyMap<string, string> | undefined;
  },
): ImageReplacementMetadata => {
  if (node.text) return { type: ImageNodeType.Unknown };

  const classes = node.classes
    .map((classId) => classIdToNameMap.get(classId))
    .filter(exists);

  if (node.tag === 'img') {
    const key = getImageKey(element, node);
    const src = node.data.attr?.['src'];

    if (src?.match(PLACEHOLDER_LOGO_URL_RE)) {
      return { type: ImageNodeType.Logo, key };
    }

    if (src?.match(OVERRIDABLE_WITHOUT_PLACEHOLDER_URL_RE)) {
      return {
        type: ImageNodeType.Overridable,
        key,
        placeholderImageSet: null,
      };
    }

    if (classes.some((c) => c.match(ICON_CLASS_RE))) {
      return { type: ImageNodeType.Icon, key };
    }

    const slug = element.source.slug;

    const standardImageSet = slug.startsWith('section_team')
      ? 'team'
      : slug.startsWith('section_testimonial')
      ? 'testimonial'
      : slug.startsWith('section_contact')
      ? 'contact'
      : slug.startsWith('section_timeline')
      ? 'timeline'
      : slug.startsWith('section_portfolio')
      ? 'portfolio'
      : 'default';

    const styles = node.classes
      .map((classId) => classIdToStyleMap?.get(classId))
      .filter(exists);

    // Detect circular images, and assume these are avatars.
    const isCircular = styles.some((s) =>
      s.match(/border-top-left-radius: 100%/),
    );
    const isBackground = classes.some((c) => c.match(/_background-image$/));

    const imageSet = isCircular
      ? 'team'
      : SPECIAL_CASES[slug] ?? (isBackground ? 'background' : standardImageSet);

    return {
      type: ImageNodeType.Overridable,
      key,
      placeholderImageSet: imageSet,
    };
  }

  if (
    node.tag === 'div' &&
    classes.some((c) => c.match(/_background-image$/))
  ) {
    const key = getImageKey(element, node);
    return {
      type: ImageNodeType.Overridable,
      key,
      placeholderImageSet: 'background',
    };
  }

  return { type: ImageNodeType.Unknown };
};

export type OverridableImage = {
  key: string;
  imageSet: keyof typeof PLACEHOLDER_IMAGE_SETS | null;
};

/**
 * Synchronous, but expects that all components in the element tree have already
 * been fetched. Use with caution! (Use findOverridableImagesInElement if you're
 * already in an async context to be safe.)
 */
export const findOverridableImagesInElementSynchronous = (
  element: ElementInstance | ElementInstanceState,
  library: LibraryCache,
  undo?: UndoManager,
) => {
  return findImagesInElementSynchronous(element, library, undo)
    .map((metadata) =>
      metadata.type === ImageNodeType.Overridable
        ? {
            key: metadata.key,
            imageSet: metadata.placeholderImageSet,
          }
        : null,
    )
    .filter(exists);
};

export const findOverridableImagesInElement = async (
  element: ElementInstance | ElementInstanceState,
  library: LibraryCache,
  undo?: UndoManager,
) => {
  const allSlugs = allSlugsInElementTree(element);
  await Promise.all(
    allSlugs.map((slug) => library.getPublicComponentJson(slug)),
  );
  return findOverridableImagesInElementSynchronous(element, library, undo);
};

export const findImagesInElement = async (
  element: ElementInstance | ElementInstanceState,
  library: LibraryCache,
  undo?: UndoManager,
): Promise<ReadonlyArray<ImageReplacementMetadata>> => {
  const allSlugs = allSlugsInElementTree(element);
  await Promise.all(
    allSlugs.map((slug) => library.getPublicComponentJson(slug)),
  );
  return findImagesInElementSynchronous(element, library, undo);
};

const findImagesInElementSynchronous = (
  element: ElementInstance | ElementInstanceState,
  library: LibraryCache,
  undo?: UndoManager,
): ReadonlyArray<ImageReplacementMetadata> => {
  const comp = library.getCachedPublicComponentJson(element.source.slug)?.value;
  if (!comp) {
    console.warn('Component was not cached:', element.source.slug);
    return [];
  }

  if (undo != null && element.mutable) {
    // Ensure slot overrides are synced before we try referring to them below.
    syncSlotOverrides({
      element,
      library,
      undo,
    });
  }

  const rootNode = extractTreeByName(comp, element.source.rootName);
  const classIdToNameMap = getClassIdToNameMap(comp.payload.styles);
  const classIdToStyleMap = getClassIdToStyleLessMap(comp.payload.styles);

  const deleteClassIds = new Set(
    comp.payload.styles
      .filter((s) => s.name.endsWith(' (Delete)'))
      .map((s) => s._id),
  );

  const images: ImageReplacementMetadata[] = [];

  const visitNode = async (node: NestedNode) => {
    if (node.text) return;

    if (node.classes.some((classId) => deleteClassIds.has(classId))) return;

    const metadata = getImageReplacementMetadata(element, node, {
      classIdToNameMap,
      classIdToStyleMap,
    });

    images.push(metadata);

    const slotInfo = getSlotInfoAtNode(node, element.source, comp);
    if (slotInfo) {
      const slotOverride = element.slotOverrides.get(slotInfo.name);
      if (slotOverride) {
        for (const child of slotOverride.items) {
          images.push(...findImagesInElementSynchronous(child, library, undo));
        }
      }
    } else {
      for (const child of node.children) {
        visitNode(child);
      }
    }
  };
  visitNode(rootNode);

  return images;
};

/**
 * For each image node in the section's root element and its descendants, a
 * placeholder image is written to the concept's images map.
 *
 * By default, existing images in the concept are not overwritten, unless the
 * `overwrite` flag is set.
 */
export const populateImagesForSection = async (
  sref: SectionRef,
  session: ProjectSession,
  concept: ConceptAPI,
  library: LibraryCache,
  { overwrite = false } = {},
): Promise<Record<string, string>> => {
  const section = unrefSection(sref, session.project);
  if (!section) return {};

  const rootElement = section.element.current;
  if (!rootElement) return {};

  const images = await findOverridableImagesInElement(
    rootElement,
    library,
    session.undo,
  );

  const usedPlaceholderIdsByImageSet = new Map<
    keyof typeof PLACEHOLDER_IMAGE_SETS,
    Set<string>
  >();

  const results: Record<string, string> = {};

  for (const { key, imageSet } of images) {
    if (imageSet === null) continue;

    const usedPlaceholderIds =
      usedPlaceholderIdsByImageSet.get(imageSet) ?? new Set();
    usedPlaceholderIdsByImageSet.set(imageSet, usedPlaceholderIds);

    if (!overwrite && concept.state.images.has(key)) {
      usedPlaceholderIds.add(key);
      continue;
    }

    let imageSetOptions = new Set(PLACEHOLDER_IMAGE_SETS[imageSet]);
    for (const imageKey of usedPlaceholderIds) {
      imageSetOptions.delete(imageKey);
    }

    if (imageSetOptions.size === 0) {
      // We've used all the images in the set... clear the set of ones we've
      // used and start again with all images as options.
      usedPlaceholderIds.clear();
      imageSetOptions = new Set(PLACEHOLDER_IMAGE_SETS[imageSet]);
    }

    const index = Math.floor(Math.random() * imageSetOptions.size);
    const placeholderId = Array.from(imageSetOptions)[index];
    usedPlaceholderIds.add(placeholderId);

    results[key] = placeholderId;
  }

  return results;
};

export const populateImagesForSections = async (
  sections: ReadonlyArray<SectionRef>,
  session: ProjectSession,
  concept: ConceptAPI,
  library: LibraryCache,
  { overwrite = false } = {},
) => {
  const results: Record<string, string> = {};

  const resultsPerSection = await Promise.all(
    // TODO: section limit?
    sections.map((sref) =>
      populateImagesForSection(sref, session, concept, library, { overwrite }),
    ),
  );

  for (const sectionResults of resultsPerSection) {
    Object.assign(results, sectionResults);
  }

  return results;
};

export const populateImagesForPage = async (
  page: Page,
  session: ProjectSession,
  concept: ConceptAPI,
  library: LibraryCache,
  { overwrite = false } = {},
) =>
  populateImagesForSections(page.sections, session, concept, library, {
    overwrite,
  });

export const getPlaceholderImageUrl = (placeholderId: string) =>
  `https://d1p38huyj6upaa.cloudfront.net/${placeholderId}.jpg`;

const PLACEHOLDER_BLURRED_IMAGE_SETS: typeof PLACEHOLDER_IMAGE_SETS = {
  default: ['blur-default-1', 'blur-default-2', 'blur-default-3'],
  team: ['blur-team-1', 'blur-team-2', 'blur-team-3'],
  testimonial: ['blur-testimonial-1', 'blur-testimonial-2'],
  contact: ['blur-contact-1', 'blur-contact-2'],
  timeline: ['blur-timeline-1', 'blur-timeline-2'],
  portfolio: ['blur-portfolio-1', 'blur-portfolio-2', 'blur-portfolio-3'],
  background: ['blur-background-1', 'blur-background-2'],
};

const getPlaceholderBlurredImageId = (placeholderId: string) => {
  const imageSet = /^(.*)-/.exec(placeholderId)?.[1];
  if (!imageSet || !(imageSet in PLACEHOLDER_IMAGE_SETS)) return null;

  const imageSetKey = imageSet as keyof typeof PLACEHOLDER_IMAGE_SETS;

  const index = PLACEHOLDER_IMAGE_SETS[imageSetKey].indexOf(placeholderId);
  if (index === -1) return null;

  return PLACEHOLDER_BLURRED_IMAGE_SETS[imageSetKey][
    index % PLACEHOLDER_BLURRED_IMAGE_SETS[imageSetKey].length
  ];
};

export const getPlaceholderBlurredImageUrl = (placeholderId: string) => {
  const blurredId = getPlaceholderBlurredImageId(placeholderId);
  return blurredId ? getPlaceholderImageUrl(blurredId) : null;
};

/**
 * Prefetches all placeholder blurred images so we can render them quickly in
 * the style guide tab while we wait for the real placeholder images to load.
 */
export const prefetchPlaceholderBlurredImages = () => {
  for (const images of Object.values(PLACEHOLDER_BLURRED_IMAGE_SETS)) {
    for (const id of images) {
      const url = getPlaceholderImageUrl(id);

      const im = new Image();
      im.src = url;
      im.onload = () => {};
    }
  }
};
